import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

import { useShiftManagement } from '@/hooks/useShiftManagement';
import { ShiftAssignModal } from './ShiftAssignModal';
import { FloatingActionBar } from './FloatingActionBar';
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable';
import { Checkbox } from '@/components/ui/checkbox';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Building2, Clock, User } from 'lucide-react';
import { formatDayGroupsForDisplay } from '@/utils/shiftFormUtils';

import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getStatusColor, StatusBadge } from '@/utils/statusUtils';

/**
 * Main page for updating shift assignments
 */
export const UpdateShiftPage = () => {
  const {
    // departmentGroups,
    selectedEmployees,
    // selectedDepartments,
    selectedEmployeeIds,
    selectedShift,
    isLoadingEmployees,
    isModalOpen,
    isUpdating,
    toggleEmployeeSelection,
    // toggleDepartmentSelection,
    // isDepartmentFullySelected,
    // isDepartmentPartiallySelected,
    openModal,
    closeModal,
    handleShiftSelection,
    updateShifts,
    employees,
    shifts,
    currentAssignments,
    refreshData
  } = useShiftManagement();

  // Custom handler for individual employee shift update
  const handleIndividualEmployeeUpdate = React.useCallback((employeeId: string) => {
    // Clear other selections
    selectedEmployees.forEach(id => {
      if (id !== employeeId) {
        toggleEmployeeSelection(id);
      }
    });
    
    // Select the target employee if not already selected
    if (!selectedEmployees.has(employeeId)) {
      toggleEmployeeSelection(employeeId);
    }
    
    // Force modal open after ensuring selection
    // Use effect-like approach with state check
    const checkAndOpenModal = () => {
      if (selectedEmployees.has(employeeId) || selectedEmployeeIds.length > 0) {
        openModal();
      } else {
        // Retry after a short delay
        setTimeout(checkAndOpenModal, 20);
      }
    };
    
    // Start checking after next tick
    setTimeout(checkAndOpenModal, 10);
  }, [selectedEmployees, selectedEmployeeIds, toggleEmployeeSelection, openModal]);

  // Filters for department and shift
  const [deptFilter, setDeptFilter] = React.useState<string>('');
  const [shiftFilter, setShiftFilter] = React.useState<string[]>([]);
  const [shiftFilterOpen, setShiftFilterOpen] = React.useState(false);
  const [deptFilterOpen, setDeptFilterOpen] = React.useState(false);
  
  // Group shifts by name
  const groupedShifts = React.useMemo(() => {
    const groups = new Map<string, typeof shifts>();
    shifts.forEach(shift => {
      if (!groups.has(shift.name)) {
        groups.set(shift.name, []);
      }
      groups.get(shift.name)!.push(shift);
    });
    return groups;
  }, [shifts]);

  // Build table rows from employees and current assignments with filters
  const tableRows = React.useMemo(() => {
    return employees
      .filter(e => !deptFilter || (e.department || '') === deptFilter)
      .filter(e => {
        if (shiftFilter.length === 0) return true;
        const currentShiftId = currentAssignments.get(e.id)?.shift_id;
        return currentShiftId && shiftFilter.includes(currentShiftId);
      })
      .map(e => ({
        id: e.id,
        name: e.name,
        employee_id: e.employee_id || '',
        department: e.department || 'Unassigned',
        position: e.position || '',
        status: e.status,
        shift_name: currentAssignments.get(e.id)?.name || '',
        shift_code: currentAssignments.get(e.id)?.code || ''
      }));
  }, [employees, deptFilter, shiftFilter, currentAssignments]);

  const allFilteredSelected = React.useMemo(() => {
    if (tableRows.length === 0) return false;
    return tableRows.every(r => selectedEmployees.has(r.id));
  }, [tableRows, selectedEmployees]);

  const toggleSelectAllFiltered = React.useCallback((checked: boolean) => {
    // Toggle selection for all filtered rows
    tableRows.forEach(r => {
      const isSelected = selectedEmployees.has(r.id);
      if (checked && !isSelected) toggleEmployeeSelection(r.id);
      if (!checked && isSelected) toggleEmployeeSelection(r.id);
    });
  }, [tableRows, selectedEmployees, toggleEmployeeSelection]);

  type Row = typeof tableRows[number];
  const columns: DataTableColumn<Row>[] = React.useMemo(() => [
    {
      key: 'id',
      header: (
        <Checkbox
          checked={allFilteredSelected}
          onCheckedChange={(v) => toggleSelectAllFiltered(!!v)}
          aria-label="Select all filtered"
        />
      ),
      sortable: false,
      searchable: false,
      className: 'w-10',
      hidden: false, // Hide from column visibility dropdown
      hideFromDropdown: true, // Remove from column visibility dropdown
      render: (_v, row) => (
        <Checkbox
          checked={selectedEmployees.has(row.id)}
          onCheckedChange={() => toggleEmployeeSelection(row.id)}
          aria-label="Select row"
        />
      )
    },
    {
      key: 'name',
      header: 'Employee Name',
      sortable: true,
      searchable: true,
      className: 'text-left',
      render: (value, row) => {
        const getInitials = (name: string): string => {
          if (!name) return '?';
          const names = name.trim().split(' ');
          if (names.length === 1) return names[0].charAt(0).toUpperCase();
          return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
        };

        return (
          <div className="flex items-center space-x-3">
            <Avatar className="h-10 w-10 flex-shrink-0 ring-2 ring-background border-2 border-border">
              <AvatarFallback className="bg-gradient-to-br from-blue-100 to-blue-200 text-blue-700 font-semibold">
                {getInitials(String(value))}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col min-w-0">
              <span className="font-semibold text-foreground truncate">{String(value)}</span>
              <span className="text-sm text-muted-foreground truncate">
                ID: {String(row.employee_id)}
              </span>
            </div>
          </div>
        );
      },
      exportRender: (value) => String(value)
    },
    // Removed Employee ID column (shown beneath name already)
    {
      key: 'department',
      header: 'Department',
      sortable: true,
      searchable: true,
      render: (value) => (
        <div className="text-center">
          <Badge variant="outline" className={`whitespace-normal ${getStatusColor('neutral', 'light')}`}>
            <Building2 className="h-3 w-3 mr-1" />
            {String(value)}
          </Badge>
        </div>
      ),
      exportRender: (value) => String(value)
    },
    {
      key: 'position',
      header: 'Position',
      sortable: true,
      searchable: true,
      render: (value) => (
        <div className="text-center">
          <Badge variant="outline" className={`whitespace-normal ${getStatusColor('info', 'light')}`}>
            <User className="h-3 w-3 mr-1" />
            {String(value)}
          </Badge>
        </div>
      ),
      exportRender: (value) => String(value)
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      searchable: true,
      render: (value) => (
        <div className="text-center">
          <StatusBadge 
            status={String(value)}
            className="whitespace-normal"
          />
        </div>
      ),
      exportRender: (value) => String(value)
    },
    {
      key: 'shift_name',
      header: 'Current Shift',
      sortable: true,
      searchable: true,
      render: (value, row) => (
        <div className="text-center">
          <Badge variant="secondary" className={`px-3 py-1.5 font-semibold ${getStatusColor('info', 'light')}`}>
            <Clock className="h-3 w-3 mr-1" />
            {String(value) || 'No Shift'}
          </Badge>
          {row.shift_code && (
            <div className="text-xs text-muted-foreground mt-1">
              Code: {row.shift_code}
            </div>
          )}
        </div>
      ),
      exportRender: (value) => String(value)
    },
    {
      key: 'actions' as unknown as keyof Row,
      header: 'Actions',
      sortable: false,
      searchable: false,
      className: 'w-32',
      render: (_value, row) => (
        <div className="text-center">
          <Button
            size="sm"
            variant="secondary"
            className="h-8 px-3 text-xs"
            onClick={() => handleIndividualEmployeeUpdate(row.id)}
          >
            Update Shift
          </Button>
        </div>
      ),
      exportRender: () => ''
    },
  ], [selectedEmployees, toggleEmployeeSelection, allFilteredSelected, toggleSelectAllFiltered, handleIndividualEmployeeUpdate]);

  return (
    <div className="rounded-lg border bg-card">
      <DataTable
        data={tableRows}
        columns={columns}
        title="System Employees"
        isLoading={isLoadingEmployees}
        config={{
          searchPlaceholder: 'Search employees...',
          defaultSortField: 'name',
          rowsPerPage: 10,
          showSearch: true,
          showExport: false,
          showColumnVisibility: true,
          showRefresh: true,
          emptyStateMessage: 'No employees found'
        }}
        onRefresh={() => refreshData()}
        toolbarChildren={
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Department</span>
              <Popover open={deptFilterOpen} onOpenChange={setDeptFilterOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={deptFilterOpen}
                    className="w-48 justify-between"
                  >
                    {deptFilter === '' ? "All departments" : deptFilter}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-48 p-0">
                  <Command>
                    <CommandInput placeholder="Search departments..." />
                    <CommandList>
                      <CommandEmpty>No department found.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          onSelect={() => {
                            setDeptFilter('');
                            setDeptFilterOpen(false);
                          }}
                          className="flex items-center justify-between"
                        >
                          <div className="flex items-center">
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                deptFilter === '' ? "opacity-100" : "opacity-0"
                              )}
                            />
                            <span className="font-medium">All departments</span>
                          </div>
                        </CommandItem>
                        {Array.from(new Set(employees.map(e => e.department || 'Unassigned'))).sort().map(dep => (
                          <CommandItem
                            key={dep}
                            onSelect={() => {
                              setDeptFilter(dep === 'Unassigned' ? '' : dep);
                              setDeptFilterOpen(false);
                            }}
                            className="flex items-center justify-between"
                          >
                            <div className="flex items-center">
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  deptFilter === (dep === 'Unassigned' ? '' : dep) ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <span className="font-medium">{dep}</span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Shift</span>
              <Popover open={shiftFilterOpen} onOpenChange={setShiftFilterOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={shiftFilterOpen}
                    className="w-48 justify-between"
                  >
                    {shiftFilter.length === 0
                      ? "All shifts"
                      : shiftFilter.length === 1
                      ? shifts.find(s => s.id === shiftFilter[0])?.name || "Selected"
                      : `${shiftFilter.length} shifts selected`}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0">
                  <Command>
                    <CommandInput placeholder="Search shifts..." />
                    <CommandList>
                      <CommandEmpty>No shift found.</CommandEmpty>
                      <CommandGroup>
                        {Array.from(groupedShifts.entries()).map(([shiftName, shiftCodes]) => (
                          <div key={shiftName} className="relative">
                            <div className="px-2 py-1.5 text-sm font-medium text-muted-foreground bg-muted/50">
                              {shiftName}
                            </div>
                            {shiftCodes.map((shift) => (
                              <CommandItem
                                key={shift.id}
                                onSelect={() => {
                                  setShiftFilter(current => {
                                    const isSelected = current.includes(shift.id);
                                    if (isSelected) {
                                      return current.filter(id => id !== shift.id);
                                    } else {
                                      return [...current, shift.id];
                                    }
                                  });
                                }}
                                className="ml-4 flex items-start justify-between py-3"
                              >
                                <div className="flex items-center min-w-0 flex-1">
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4 mt-0.5 flex-shrink-0",
                                      shiftFilter.includes(shift.id) ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  <span className="font-medium text-sm">{shift.code || 'No Code'}</span>
                                </div>
                                {shift.day_time_deduction && shift.day_time_deduction.length > 0 && (
                                  <div className="text-xs text-muted-foreground ml-2 text-right min-w-0 flex-shrink-0 max-w-[200px] leading-relaxed space-y-1">
                                    {formatDayGroupsForDisplay(shift.day_time_deduction).map((schedule, index) => (
                                      <div key={index} className="text-right">
                                        {schedule}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </CommandItem>
                            ))}
                          </div>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        }
      />

      {/* Floating Action Bar */}
      <FloatingActionBar
        selectedCount={selectedEmployeeIds.length}
        onUpdateShift={openModal}
        isUpdating={isUpdating}
      />

      {/* Shift Assignment Modal */}
      <ShiftAssignModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onUpdate={updateShifts}
        selectedEmployeeCount={selectedEmployeeIds.length}
        isUpdating={isUpdating}
        selectedShift={selectedShift}
        onShiftSelect={handleShiftSelection}
      />
    </div>
  );
}

UpdateShiftPage.displayName = 'UpdateShiftPage'; 